/**
 * Projects Data - Braian's Featured Work
 * Real projects with metrics and technologies
 */

export const projects = [
  {
    id: 1,
    title: "SmartEscrow - Multi-Tenant Fintech Platform",
    slug: "smartescrow",
    description: "White-label escrow and financing platform serving multiple brands with isolated data, automated compliance, real-time transaction monitoring, and SSO/OAuth2 integration.",
    longDescription: "As CTO, led the design and development of a sophisticated multi-tenant fintech platform for direct purchase order financing without personal guarantees. The system features complete brand isolation, automated financial workflows, role-based access control across multiple domains, and integrations with payment gateways.",

    // Technologies used
    tech: ["Symfony 7", "Doctrine", "Vue.js", "Pinia", "PostgreSQL", "Docker", "OAuth2", "Nginx", "RabbitMQ"],

    // Project metrics
    metrics: "Processing $2M+ monthly transactions",
    metricsDetails: {
      transactions: "$2M+ monthly",
      uptime: "99.9%",
      clients: "Multiple brands",
      users: "1000+ active users"
    },

    // Project type & category
    type: "fintech",
    category: "Full-Stack + Leadership",

    // Visual styling
    gradient: "from-emerald-500/20 to-cyan-500/20",
    accentColor: "#00d4aa",

    // Links
    link: null, // Private platform
    caseStudy: null,

    // Role & responsibilities
    role: "CTO / Lead Developer",
    responsibilities: [
      "Architectural design and technical strategy",
      "Team leadership and coordination",
      "Full-stack development (Symfony + Vue.js)",
      "DevOps setup (Docker, Nginx, multi-domain)",
      "OAuth2/SSO implementation",
      "Security and compliance measures",
      "Performance optimization"
    ],

    // Timeline
    period: "2023 - Present",
    duration: "2+ years",

    // Status
    status: "active",
    featured: true
  },

  {
    id: 2,
    title: "Burger King + Monster - Promo Campaign",
    slug: "burger-king-monster",
    description: "Landing page and ranking system for code exchange promotional campaign. 6-month campaign with focus on responsive design, fresh UX, and seamless mobile experience.",
    longDescription: "Developed a complete promotional platform for Burger King's collaboration with Monster Energy. The system handled code validation, user rankings, prize distribution logic, and real-time leaderboards.",

    tech: ["Symfony", "Vue.js", "MySQL", "Responsive Design", "REST API"],

    metrics: "6-month campaign, 10K+ participants",
    metricsDetails: {
      participants: "10,000+",
      duration: "6 months",
      codesProcessed: "50,000+",
      uptime: "100%"
    },

    type: "fullstack",
    category: "Frontend-focused",
    gradient: "from-orange-500/20 to-red-500/20",
    accentColor: "#ff6b35",

    role: "Full-Stack Developer",
    period: "2020",
    duration: "3 months",
    status: "completed",
    featured: false
  },
  {
    id: 3,
    title: "Flipsimply - Crowdfunding Platform",
    slug: "flipsimply",
    description: "Full animated crowdfunding platform developed from scratch. First major end-to-end project after transitioning to remote work. Features project creation, funding workflows, and user dashboards.",
    longDescription: "Complete crowdfunding solution with campaign management, payment processing integration, real-time funding progress, user authentication, and admin dashboard for campaign oversight.",

    tech: ["Symfony", "Vue.js", "MySQL", "Payment Gateway", "Docker"],

    metrics: "Successful funding for 50+ projects",
    metricsDetails: {
      projects: "50+ funded",
      users: "5,000+",
      fundsProcessed: "$500K+"
    },

    type: "fullstack",
    category: "Full-Stack",
    gradient: "from-violet-500/20 to-purple-500/20",
    accentColor: "#8b5cf6",

    role: "Full-Stack Developer",
    period: "2020",
    duration: "6 months",
    status: "completed",
    featured: true
  },

  {
    id: 4,
    title: "Lanzanos - Legacy Migration",
    slug: "lanzanos",
    description: "Maintenance and modernization of legacy crowdfunding platform. Containerized outdated Ubuntu 12.04 / Python 2.7 system into modern Docker infrastructure without downtime.",
    longDescription: "Led the complex migration of a decade-old crowdfunding system running on obsolete infrastructure. Dockerized the application, upgraded dependencies where possible, and established modern DevOps practices while maintaining 100% uptime.",

    tech: ["Python", "Django", "Docker", "Ubuntu", "Nginx", "PostgreSQL", "Migration Scripts"],

    metrics: "Zero downtime migration, 10+ year old system",
    metricsDetails: {
      downtime: "0 hours",
      dataIntegrity: "100%",
      systemAge: "10+ years",
      modernization: "Ubuntu 12.04 → Docker"
    },

    type: "devops",
    category: "DevOps + Maintenance",
    gradient: "from-blue-500/20 to-indigo-500/20",
    accentColor: "#3b82f6",

    role: "DevOps Engineer / Maintainer",
    period: "2023",
    duration: "4 months",
    status: "completed",
    featured: true
  },

  {
    id: 5,
    title: "42mate Web Redesign",
    slug: "42mate",
    description: "Complete redesign and redevelopment of 42mate's corporate website from scratch using Drupal 7 + Vue.js. Improved architecture, modern frontend, and enhanced user experience.",
    longDescription: "Full website overhaul including information architecture, responsive design, CMS integration, and Vue.js components for dynamic sections. Also handled ongoing maintenance of related projects like Skybrary.",

    tech: ["Drupal 7", "Vue.js", "PHP", "MySQL", "jQuery", "Responsive Design"],

    metrics: "Daily maintenance + feature development",

    type: "fullstack",
    category: "CMS + Frontend",
    gradient: "from-teal-500/20 to-green-500/20",
    accentColor: "#14b8a6",

    role: "Full-Stack Developer",
    period: "2019-2020",
    duration: "1+ year",
    status: "completed",
    featured: false
  },

  {
    id: 6,
    title: "MinIO Migration Suite",
    slug: "minio-migration",
    description: "Automated migration scripts for transitioning 18 MinIO buckets (472GB) across Debian 12→13 server upgrades with rollback capabilities and zero data loss.",
    longDescription: "Developed robust migration tooling for critical object storage infrastructure. Included pre-migration validation, parallel transfer optimization, integrity checking, and automated rollback procedures.",

    tech: ["Bash", "MinIO", "Debian", "Linux", "Automation Scripts"],

    metrics: "18 buckets, 472GB migrated safely",
    metricsDetails: {
      buckets: 18,
      dataSize: "472GB",
      dataLoss: "0%",
      downtime: "< 2 hours"
    },

    type: "devops",
    category: "Infrastructure",
    gradient: "from-cyan-500/20 to-blue-500/20",
    accentColor: "#06b6d4",

    role: "DevOps Engineer",
    period: "2024",
    duration: "1 month",
    status: "completed",
    featured: false
  },
  {
    id: 7,
    title: "SmartEscrow SSO - OAuth2 / SSO Platform",
    slug: "smartescrow-sso",
    description: "Centralized OAuth2 / SSO platform enabling secure authentication, role synchronization, and multi-domain access across all SmartEscrow products.",
    longDescription: "Designed and implemented a centralized Single Sign-On platform to unify authentication across multiple fintech products and white-label domains. The system manages OAuth2 flows, token lifecycle, user-role synchronization, platform access control, and secure redirects between environments (prod, staging, local). Built as a reusable SDK and Symfony bundle to ensure consistency and scalability across services.",

    tech: [
      "Symfony",
      "OAuth2",
      "JWT",
      "PostgreSQL",
      "Doctrine",
      "Docker",
      "Nginx",
      "Security Components"
    ],

    metrics: "SSO for 6+ platforms and brands",
    metricsDetails: {
      platforms: "6+ integrated services",
      domains: "Multi-domain support",
      authFlows: "Authorization Code + Refresh Token",
      users: "Shared user base across platforms"
    },

    type: "backend",
    category: "Security + Architecture",

    gradient: "from-sky-500/20 to-indigo-500/20",
    accentColor: "#6366f1",

    link: null,
    caseStudy: null,

    role: "CTO / Architect",
    responsibilities: [
      "OAuth2 architecture and security design",
      "SSO SDK and Symfony bundle development",
      "Role and permission synchronization",
      "Multi-domain redirect handling",
      "Environment isolation (prod / staging / local)",
      "Token lifecycle and refresh strategy"
    ],

    period: "2023 - Present",
    duration: "1.5+ years",

    status: "active",
    featured: true
  },
  {
    id: 8,
    title: "SmartEscrow Wallet - Virtual Wallet System",
    slug: "smartescrow-wallet",
    description: "Multi-tenant virtual wallet system handling balances, internal transfers, and financial flows between investors, financed users, and the platform.",
    longDescription: "Developed the core virtual wallet infrastructure used across SmartEscrow products. The system manages balances, internal money movements, platform fees, investor payouts, and financial traceability. Integrated tightly with actions, statistics, and audit layers to ensure consistent financial state across operations.",

    tech: [
      "Symfony",
      "PostgreSQL",
      "Doctrine",
      "Docker",
      "RabbitMQ",
      "Event-driven Architecture"
    ],

    metrics: "Core financial engine for SmartEscrow",
    metricsDetails: {
      balanceTracking: "Real-time",
      supportedFlows: "Investor, Financed, Platform",
      consistency: "Strict accounting rules",
      auditability: "Full traceability"
    },

    type: "backend",
    category: "Fintech Core",

    gradient: "from-emerald-500/20 to-green-600/20",
    accentColor: "#10b981",

    link: null,
    caseStudy: null,

    role: "CTO / Lead Backend Developer",
    responsibilities: [
      "Wallet domain modeling",
      "Money flow rules definition",
      "Balance consistency enforcement",
      "Integration with actions and statistics",
      "Edge-case handling (returns, excess, penalties)",
      "Performance and data integrity"
    ],

    period: "2023 - Present",
    duration: "2+ years",

    status: "active",
    featured: true
  },
  {
    id: 9,
    title: "SmartEscrow Factoring Platform",
    slug: "smartescrow-factoring",
    description: "Factoring and purchase-order financing platform supporting investors, financed companies, and automated financial operations without personal guarantees.",
    longDescription: "Built and evolved the factoring platform enabling companies to obtain financing through investors under a controlled escrow model. Includes operation lifecycle management, financial actions, document handling, statistics generation, and role-based access for investors, financed users, and administrators.",

    tech: [
      "Symfony 7",
      "PostgreSQL",
      "Doctrine",
      "Vue.js",
      "EasyAdmin",
      "Docker",
      "RabbitMQ"
    ],

    metrics: "Production-grade factoring operations",
    metricsDetails: {
      operations: "Multiple active investments",
      roles: "Investor / Financed / Admin",
      automation: "Financial actions & statistics",
      reliability: "Production usage"
    },

    type: "fintech",
    category: "Full-Stack Fintech",

    gradient: "from-amber-500/20 to-orange-500/20",
    accentColor: "#f59e0b",

    link: null,
    caseStudy: null,

    role: "CTO / Full-Stack Architect",
    responsibilities: [
      "Factoring business logic design",
      "Investment lifecycle modeling",
      "Financial actions and rules engine",
      "Statistics and reporting system",
      "Document management flows",
      "Admin tooling and permissions"
    ],

    period: "2023 - Present",
    duration: "2+ years",

    status: "active",
    featured: true
  },
  {
    id: 10,
    title: "SmartEscrow Integration Bridge",
    slug: "smartescrow-bridge",
    description: "Internal event-driven integration layer enabling synchronization and communication between SmartEscrow services.",
    longDescription: "Designed and implemented an internal integration bridge responsible for coordinating data flow between multiple SmartEscrow services. The system handles asynchronous events, background workers, and cross-service synchronization to maintain consistency across the ecosystem.",

    tech: [
      "Symfony",
      "RabbitMQ",
      "Messenger",
      "Docker",
      "Event-driven Architecture"
    ],

    metrics: "Core internal integration layer",
    metricsDetails: {
      messaging: "Asynchronous",
      reliability: "Production-grade",
      scope: "Cross-service synchronization"
    },

    type: "backend",
    category: "Integration + Messaging",

    gradient: "from-fuchsia-500/20 to-purple-600/20",
    accentColor: "#a855f7",

    link: null,
    caseStudy: null,

    role: "CTO / Backend Architect",
    responsibilities: [
      "Event-driven architecture design",
      "Message queue integration",
      "Worker and retry strategies",
      "Cross-service consistency",
      "Operational reliability"
    ],

    period: "2023 - Present",
    duration: "1+ year",

    status: "active",
    featured: false
  }
  ,
  {
    id: 11,
    title: "Public Registry Processing Pipeline",
    slug: "registro-publico-processor",
    description: "Automated processing pipeline for public corporate registry data, including parsing, normalization, and persistence.",
    longDescription: "Built a backend processing system for ingesting and transforming public registry data (Registro Público / BORME). The pipeline automates data extraction, normalization, validation, and storage, supporting downstream financial and compliance workflows.",

    tech: [
      "PHP",
      "Symfony",
      "PostgreSQL",
      "CLI Scripts",
      "Automation"
    ],

    metrics: "Automated public data ingestion",
    metricsDetails: {
      sources: "Public registries",
      processing: "Scripted & repeatable",
      reliability: "Deterministic pipelines"
    },

    type: "backend",
    category: "Data Processing",

    gradient: "from-slate-500/20 to-gray-600/20",
    accentColor: "#64748b",

    link: null,
    caseStudy: null,

    role: "Backend Engineer",
    responsibilities: [
      "Data ingestion pipelines",
      "Parsing and normalization",
      "Automation scripting",
      "Data integrity checks"
    ],

    period: "2024",
    duration: "Ongoing",

    status: "active",
    featured: false
  }
  ,
  {
    id: 12,
    title: "BORME / BOE Document Processor",
    slug: "function-borme",
    description: "Automated document processing and parsing system for official gazette publications.",
    longDescription: "Developed automation tooling for downloading, parsing, and extracting structured data from official gazette publications (BORME / BOE). Focused on reliability, repeatability, and clean data extraction from semi-structured PDF sources.",

    tech: [
      "PHP",
      "PDF Processing",
      "CLI Scripts",
      "Data Extraction"
    ],

    metrics: "Automated official document parsing",
    metricsDetails: {
      documentTypes: "PDF gazettes",
      extraction: "Structured data",
      automation: "Fully scripted"
    },

    type: "backend",
    category: "Data Engineering",

    gradient: "from-neutral-500/20 to-zinc-600/20",
    accentColor: "#71717a",

    link: null,
    caseStudy: null,

    role: "Backend Engineer",
    responsibilities: [
      "PDF parsing logic",
      "Data extraction rules",
      "Automation scripts",
      "Error handling for malformed sources"
    ],

    period: "2024",
    duration: "Ongoing",

    status: "active",
    featured: false
  }
  ,
  {
    id: 13,
    title: "Financial Intelligence APIs",
    slug: "apis-financialintelligence",
    description: "Backend APIs providing financial analysis and structured data to support internal decision-making processes.",
    longDescription: "Developed internal APIs focused on aggregating, exposing, and analyzing financial data to support operational and strategic decision-making. Designed for extensibility and integration with existing fintech services.",

    tech: [
      "PHP",
      "Symfony",
      "REST APIs",
      "PostgreSQL"
    ],

    metrics: "Internal financial analysis tooling",
    metricsDetails: {
      usage: "Internal services",
      focus: "Decision support",
      integration: "Service-oriented"
    },

    type: "backend",
    category: "Financial Data",

    gradient: "from-rose-500/20 to-pink-600/20",
    accentColor: "#ec4899",

    link: null,
    caseStudy: null,

    role: "Backend Engineer",
    responsibilities: [
      "API design",
      "Financial data modeling",
      "Integration with core systems",
      "Data consistency"
    ],

    period: "2024",
    duration: "Ongoing",

    status: "active",
    featured: false
  }
];

// Filter helpers
export const getFeaturedProjects = () => projects.filter(p => p.featured);
export const getNonFeaturedProjects = () => projects.filter(p => !p.featured);
export const getProjectsByType = (type) => projects.filter(p => p.type === type);
export const getActiveProjects = () => projects.filter(p => p.status === 'active');

export default projects;
